import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported } from 'firebase/analytics';
import {
  OAuthProvider,
  connectAuthEmulator,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { firebaseConfig, utahIdProviderId } from '../firebaseConfig';
import { setCachedToken } from './tokenStore';
import { setIdpIdToken } from './idpLogout';
import { debugLog } from '../debug/devLogger';

// Re-export the token cache so existing importers of firebaseAuth keep working.
export { getCachedToken, setCachedToken } from './tokenStore';

/**
 * Framework-agnostic Firebase Authentication wrapper.
 *
 * Auth model: Firebase Authentication is the SDK; id.utah.gov is wired into the
 * Firebase project as a *custom OIDC provider*. Sign-in is the redirect flow
 * (`signInWithRedirect`) and, after the bounce back, `getRedirectResult`
 * completes it. The resulting Firebase ID token is attached as
 * `Authorization: Bearer <token>` on API/GraphQL calls (see graphQLWebservice).
 */

// Guard against double-init (React Strict Mode / HMR / repeated imports).
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
debugLog('auth', 'firebaseAuth: app initialized', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  reused: getApps().length > 1,
});

export const auth = getAuth(app);

// Back-compat alias of the initialized Auth instance for any framework-agnostic
// importer that referenced `firebaseAuth.sdk`. The React hook for consuming auth
// state (`useFirebaseAuth`) lives in FirebaseAuthProvider — this module stays
// framework-agnostic (no React hooks), so it can be imported by non-React code
// (webservice token attachment, analytics, header config).
export const sdk = auth;

// Firebase Analytics (GA4). Analytics is browser-only and throws in
// unsupported environments (SSR, unsupported browsers, the jsdom test env), so
// it is initialized lazily behind isSupported() and only when a measurementId
// is configured. Any failure is swallowed — analytics must never block app or
// auth startup. Exposed as a Promise resolving to the Analytics instance (or
// null) so callers can await initialization without racing it.
export const analyticsReady = isSupported()
  .then((supported) =>
    supported && firebaseConfig.measurementId ? getAnalytics(app) : null,
  )
  .then((instance) => {
    if (instance) {
      debugLog('auth', 'firebaseAuth: analytics initialized', {
        measurementId: firebaseConfig.measurementId,
      });
    }
    return instance;
  })
  .catch(() => null);

// The id.utah.gov custom OIDC provider. `utahIdProviderId` already carries the
// required `oidc.` prefix.
export const provider = new OAuthProvider(utahIdProviderId);
debugLog('auth', 'firebaseAuth: OIDC provider created', {
  providerId: utahIdProviderId,
});

// Request the standard OIDC scopes so id.utah.gov releases the user's identity
// claims. Without `email` the provider does not federate an email claim, so the
// Firebase ID token (and `user.email`) come back empty and the API cannot match
// or provision a pharma-user by email.
provider.addScope('openid');
provider.addScope('email');
provider.addScope('profile');

// Force the IdP to RE-AUTHENTICATE on every sign-in. The Utah ID (Ping) logout
// (`mylogin.utah.gov/logout`) ends the Ping session but leaves the underlying
// ForgeRock AM SSO session at `sso.mylogin.utah.gov` alive, so without this a
// post-logout sign-in silently reuses that session (the AM `idFromSession`
// check succeeds and no credential prompt is shown). The OIDC `prompt=login`
// parameter instructs AM to force authentication regardless of an existing
// session, so signing out then back in always requires credentials.
provider.setCustomParameters({ prompt: 'login' });
debugLog('auth', 'firebaseAuth: provider forces prompt=login (re-auth)');

// Connect to the local Firebase Auth emulator when developing locally.
if (
  typeof process !== 'undefined' &&
  process.env?.NODE_ENV === 'development' &&
  !auth.emulatorConfig
) {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
      disableWarnings: true,
    });
    debugLog('auth', 'firebaseAuth: connected to local Auth emulator');
  } catch {
    /* emulator not running — ignore in non-emulated dev */
    debugLog('auth', 'firebaseAuth: Auth emulator not available (ignored)');
  }
}

/** Begin the id.utah.gov OIDC sign-in (full-page redirect). */
export const signIn = () => {
  debugLog('auth', 'signIn: starting signInWithRedirect', {
    providerId: utahIdProviderId,
  });
  return signInWithRedirect(auth, provider);
};

/**
 * Complete a redirect sign-in after the bounce back from id.utah.gov.
 * Returns the UserCredential (or null when there is no pending redirect).
 */
export const completeRedirect = async () => {
  debugLog('auth', 'completeRedirect: calling getRedirectResult');
  const result = await getRedirectResult(auth);
  debugLog('auth', 'completeRedirect: getRedirectResult returned', {
    hasResult: Boolean(result),
    uid: result?.user?.uid,
    email: result?.user?.email,
  });
  // Capture the id.utah.gov id_token from the OIDC credential — it is the
  // `id_token_hint` AM requires to end the SSO session at logout (Firebase keeps
  // only its own token, so we must grab the provider id_token here). Persisted by
  // setIdpIdToken so it survives the reload before a later sign-out.
  if (result) {
    try {
      const credential = OAuthProvider.credentialFromResult(result);
      if (credential?.idToken) {
        setIdpIdToken(credential.idToken);
      }
    } catch {
      /* no OIDC credential on this result — logout falls back to local sign-out */
    }
  }
  return result;
};

/** Subscribe to auth-state changes. Returns the unsubscribe function. */
export const subscribeAuth = (callback) => {
  debugLog('auth', 'subscribeAuth: registering onAuthStateChanged listener');
  return onAuthStateChanged(auth, callback);
};

/** Resolve when the initial auth state has been determined. */
export const authReady = () => {
  debugLog('auth', 'authReady: awaiting initial auth state');
  return auth.authStateReady();
};

/** Sign the user out of Firebase. */
export const signOutFirebase = () => {
  debugLog('auth', 'signOutFirebase: clearing cached token + signOut(auth)');
  setCachedToken(null);
  return signOut(auth);
};

/**
 * Return a valid Firebase ID token for the current user (or null when signed
 * out). Pass `forceRefresh` to bypass the SDK token cache. Also refreshes the
 * synchronous `cachedToken`.
 */
export const getValidToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) {
    debugLog('auth', 'getValidToken: no current user — returning null');
    setCachedToken(null);
    return null;
  }
  const token = await user.getIdToken(forceRefresh);
  debugLog('auth', 'getValidToken: token resolved', {
    forceRefresh,
    hasToken: Boolean(token),
    uid: user.uid,
  });
  setCachedToken(token);
  return token;
};

export default auth;
